import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LicenseVerificationService } from 'src/app/services/license-verification/license-verification.service';
import LICENSE_SECTIONS_CONFIG from 'src/assets/data/license-sections-config.json';

export interface InfoItem {
  label: string;
  value: string;
  icon?: string;
  copyable?: boolean;
}

export interface SectionRow {
  label: string;
  value: string;
  status?: 'allowed' | 'not-allowed' | 'restricted' | 'info';
}

export interface LicenseSection {
  title: string;
  rows: SectionRow[];
}


@Component({
  selector: 'ipm-license-validation',
  templateUrl: './license-validation.component.html',
  styleUrls: ['./license-validation.component.scss']
})
export class LicenseValidationComponent implements OnInit {
  licenseType: string | null = null;
  licenseId: string | null = null;
  isLoading = true;
  errorMessage: string | null = null;

  licenseInfoItems: InfoItem[] = [];
  licenseSections: LicenseSection[] = [];
  licenseStatus: string = 'active';
  beatsList: any[] = [];
  sound: any = null;


  constructor(
    private route: ActivatedRoute,
    private licenseService: LicenseVerificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const typeAndId = params['typeAndId'];
      if (!typeAndId) {
        this.isLoading = false;
        this.errorMessage = 'Invalid URL';
        return;
      }

      const parts = typeAndId.split('-');
      if (parts.length < 2) {
        this.isLoading = false;
        this.errorMessage = 'Invalid URL format';
        return;
      }

      const type = parts[0];
      const id = parts.slice(1).join('-');

      if (type !== 'bl' && type !== 'sl') {
        this.isLoading = false;
        this.errorMessage = 'Invalid license type';
        return;
      }

      this.licenseType = type;
      this.licenseId = id;
      this.validateLicense();
    });
  }

  private validateLicense(): void {
    const type = this.licenseType === 'bl' ? 'beat' : 'sound';
    const request = this.licenseService.validateBeatSoundLicense(this.licenseId!, type);

    request.subscribe({
      next: (response) => {
        const data = response?.data || response;
        this.mapLicenseData(data);
        this.beatsList = data.beat ? [data.beat] : [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'License not found';
      }
    });
  }

  private mapLicenseData(data: any): void {
    this.licenseStatus = (data.status || '').toLowerCase();
    const slug = data.licence_template?.slug || 'wav';
    const isBeat = data.licence_type === 'beat';
    const itemTitle = data.beat?.title || data.sound?.title || 'N/A';
    const itemLabel = isBeat ? 'BEAT' : 'SOUND';
    const licenseId = `${isBeat ? slug.toUpperCase() : 'SOUND'}-${data.licence_number}`;
    const itemId = isBeat
      ? (data.beat?.serial_number ? `BT-${data.beat.serial_number}` : 'N/A')
      : (data.sound?.serial_number ? `SK-${data.sound.serial_number}` : 'N/A');
    const period = data.licence_period_in_year && data.licence_template?.title !== 'Negotiated Exclusive'
      ? `${data.licence_period_in_year} Year(s)`
      : 'Perpetual';

    // License Nature & Format label
    let licenseNatureFormat: string;
    if (!isBeat) {
      licenseNatureFormat = 'NON-EXCLUSIVE (WAV)';
    } else {
      const slugLower = slug.toLowerCase();
      if (slugLower === 'exclusive') {
        licenseNatureFormat = 'EXCLUSIVE';
      } else if (slugLower === 'wav_stem' || slugLower === 'wav-stem') {
        licenseNatureFormat = 'NON-EXCLUSIVE (WAV + STEMS)';
      } else {
        licenseNatureFormat = 'NON-EXCLUSIVE (WAV)';
      }
    }
    // sound object 
    if(data.sound){
      this.sound = {
        title: data.sound.title,
        serial_number: data.sound.serial_number,
        cover_image_url: data.sound.cover_image_url,
        category: data.sound.category,
      };
    }
    // 1. Top info grid (2×5 = 10 items)
    this.licenseInfoItems = [
      { label: 'LICENSE ID',           value: licenseId, copyable: true },
      { label: 'LICENSE NATURE & FORMAT', value: licenseNatureFormat },
      { label: 'EFFECTIVE DATE',        value: this.formatDate(data.issued_date) },
      { label: 'LICENSOR / SELLER',     value: data.seller?.stage_name || data.seller?.name || 'N/A' },
      { label: `${itemLabel} TITLE`,    value: itemTitle },
      { label: `${itemLabel} ID`,       value: itemId },
      { label: 'LICENSE FEE',           value: data.amount_paid ? `₹${data.amount_paid.toLocaleString()}/-` : 'N/A' },
      { label: 'LICENSE PERIOD',        value: period },
      { label: 'LICENSEE / BUYER',      value: data.buyer?.name || 'N/A' },
      { label: 'TERRITORY',             value: data.territory || 'Worldwide' },
    ];

    // 2. Middle sections — clone config and inject dynamic values
    const configKey = isBeat
      ? slug.toUpperCase().replace(/-/g, '_')  // "wav_stem" → "WAV_STEM"
      : 'SOUND';
    const config = (LICENSE_SECTIONS_CONFIG as any)[configKey] ?? (LICENSE_SECTIONS_CONFIG as any)['WAV'];

    const licenseNature = licenseNatureFormat;
    const expiryDate = data.end_date ? this.formatDate(data.end_date) : 'Perpetual';
    const sellerShare = data.seller_publishing_share != null ? `${data.seller_publishing_share}%` : 'N/A';
    const masterRecordings = data.no_of_master_recordings != null ? String(data.no_of_master_recordings) : 'N/A';

    const dynamicMap: Record<string, string> = {
      'License Nature':                   licenseNature,
      'License Expiry Date':              expiryDate,
      'Seller Publishing Share':          sellerShare,
      'No. of Master Recordings Allowed': masterRecordings,
    };

    this.licenseSections = (config as LicenseSection[]).map((section: LicenseSection) => ({
      ...section,
      rows: section.rows.map((row: SectionRow) =>
        dynamicMap[row.label] !== undefined
          ? { ...row, value: dynamicMap[row.label] }
          : { ...row }
      )
    }));
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).catch(err => console.error('Copy failed:', err));
  }

  goToSoundDetails(serialNumber: number | string): void {
    if (!serialNumber) return;
    this.router.navigate([`/sk-${serialNumber}`]);
  }
}
